/* eslint-disable react/prop-types */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Box, Button, Chip, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { ICrossWithServiceInfo } from 'features/transaction-details/types';
import { CrossActionContext, TransactionViewContext } from 'features/transaction-details/types/transactionCrossContext';
import {
	groupCrossesByEticket,
	AuthorizationTicketGroup,
	eticketsMatch,
} from 'features/transaction-details/helpers/authorizationTickets';
import { ETicketServiceLink } from '../ETicketServiceLink';
import { formatTransactionAmount } from 'features/transaction-details/helpers';
import { formatDateTimeToShowUser } from 'features/common/helpers';
import { getCrossStateName } from 'features/authorization/helpers';
import { CrossStateEnum } from 'features/authorization/types';

interface AuthorizationTicketsListProps {
	crosses: ICrossWithServiceInfo[];
	authorizationId: number;
	coinCode: string;
	highlightEticket?: string;
	/** Enfoca el botón copiar del e-ticket resaltado (deep link). */
	focusHighlightedEticketCopy?: boolean;
	operators: { value: string; description: string }[];
	transactionContext?: TransactionViewContext;
	pendingToCross?: number;
	canCross?: boolean;
	onCopySuccess: (message: string) => void;
	onUnbindCross?: (cross: ICrossWithServiceInfo) => void;
	onAddETicket?: () => void;
	onNavigationError?: (message: string) => void;
}

const buildCrossContext = (cross: ICrossWithServiceInfo, authorizationId: number): CrossActionContext => ({
	crossId: cross.id,
	authorizationId,
	amount: cross.amount,
	coinCode: cross.coinCode,
	canUnbind: cross.state === CrossStateEnum.CROSS && cross.amount > 0,
});

const TicketHistoryTimeline = ({
	ticket,
	getOperatorName,
}: {
	ticket: AuthorizationTicketGroup;
	getOperatorName: (code: string) => string;
}): JSX.Element => (
	<Box sx={{ pl: 1.5, borderLeft: 2, borderColor: 'divider', ml: 0.5, my: 0.5 }}>
		{ticket.history.map((event) => {
			const isUnbind = event.state === CrossStateEnum.UNBIND || event.amount < 0;
			const amountLabel = `${event.amount > 0 ? '+' : ''}${event.amount} ${event.coinCode}`;
			return (
				<Box
					key={event.id}
					sx={{
						display: 'flex',
						alignItems: 'center',
						flexWrap: 'wrap',
						gap: 0.75,
						py: 0.35,
					}}
				>
					<Typography variant="caption" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
						{formatDateTimeToShowUser(event.createdAt)}
					</Typography>
					<Chip
						label={getCrossStateName(event.state)}
						size="small"
						color={isUnbind ? 'error' : 'success'}
						variant={isUnbind ? 'filled' : 'outlined'}
						sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
					/>
					<Typography variant="caption" fontWeight={600} color={isUnbind ? 'error.main' : 'success.main'}>
						{amountLabel}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{getOperatorName(event.operator)} · {event.createdBy}
					</Typography>
				</Box>
			);
		})}
	</Box>
);

export const AuthorizationTicketsList = ({
	crosses,
	authorizationId,
	coinCode,
	highlightEticket,
	focusHighlightedEticketCopy = false,
	operators,
	transactionContext,
	pendingToCross = 0,
	canCross = false,
	onCopySuccess,
	onUnbindCross,
	onAddETicket,
	onNavigationError,
}: AuthorizationTicketsListProps): JSX.Element => {
	const tickets = useMemo(() => groupCrossesByEticket(crosses), [crosses]);
	const [expandedEticket, setExpandedEticket] = useState<string | false>(false);
	const highlightedTicketRef = useRef<HTMLDivElement>(null);

	const getOperatorName = (operatorCode: string): string => {
		const operator = operators.find((op) => op.value === operatorCode);
		return operator ? operator.description : operatorCode;
	};

	const fmt = (amount: number): string => formatTransactionAmount(amount, coinCode);

	const matchingTicket = useMemo(
		() => (highlightEticket ? tickets.find((t) => eticketsMatch(t.eticket, highlightEticket)) : undefined),
		[highlightEticket, tickets],
	);

	useEffect(() => {
		if (!matchingTicket) return;
		if (matchingTicket.history.length > 1) {
			setExpandedEticket(matchingTicket.eticket);
		}
	}, [matchingTicket]);

	useEffect(() => {
		if (!matchingTicket || !focusHighlightedEticketCopy) return;
		const timer = window.setTimeout(() => {
			highlightedTicketRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}, 400);
		return () => window.clearTimeout(timer);
	}, [matchingTicket, focusHighlightedEticketCopy]);

	if (tickets.length === 0) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexWrap: 'wrap',
					gap: 1,
					py: 1,
					px: 1.5,
					borderRadius: 1,
					border: 1,
					borderColor: 'divider',
					borderStyle: 'dashed',
				}}
			>
				<Typography variant="body2" color="text.secondary">
					Sin boletos cruzados
					{pendingToCross > 0 ? ` · ${fmt(pendingToCross)} disponible para cruzar` : ''}
				</Typography>
				{canCross && onAddETicket && (
					<Button size="small" variant="outlined" startIcon={<ReceiptIcon />} onClick={onAddETicket}>
						Agregar E-Ticket
					</Button>
				)}
			</Box>
		);
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
			{tickets.map((ticket) => {
				const isHighlighted = Boolean(highlightEticket && eticketsMatch(ticket.eticket, highlightEticket));
				const isCrossed = ticket.status === 'crossed';
				const activeCross = ticket.activeCross;
				const historyOpen = expandedEticket === ticket.eticket;
				const crossContext = activeCross
					? buildCrossContext(activeCross, authorizationId)
					: buildCrossContext(ticket.history[ticket.history.length - 1], authorizationId);

				return (
					<Box
						key={ticket.eticket}
						ref={isHighlighted ? highlightedTicketRef : undefined}
						sx={{
							border: 1,
							borderColor: isHighlighted ? 'primary.main' : 'divider',
							borderLeftWidth: 3,
							borderLeftColor: isCrossed ? 'success.main' : 'grey.400',
							borderRadius: 1,
							overflow: 'hidden',
							bgcolor: isHighlighted ? 'primary.50' : 'background.paper',
							boxShadow: isHighlighted ? 2 : 0,
						}}
					>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '1fr auto auto auto',
								alignItems: 'center',
								gap: 0.5,
								px: 1.5,
								py: 0.75,
							}}
						>
							<Box sx={{ minWidth: 0 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
									<ETicketServiceLink
										compact
										eticket={ticket.eticket}
										transactionContext={transactionContext}
										crossContext={{
											...crossContext,
											amount: isCrossed ? ticket.netAmount : crossContext.amount,
											canUnbind: Boolean(isCrossed && activeCross && onUnbindCross),
										}}
										onCopySuccess={onCopySuccess}
										autoFocusCopy={isHighlighted && focusHighlightedEticketCopy}
										onRequestUnbind={
											isCrossed && activeCross && onUnbindCross ? () => onUnbindCross(activeCross) : undefined
										}
										onNavigationError={onNavigationError}
									/>
									<Chip
										label={isCrossed ? 'Cruzado' : 'Descruzado'}
										size="small"
										color={isCrossed ? 'success' : 'default'}
										variant={isCrossed ? 'filled' : 'outlined'}
										sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.75rem' } }}
									/>
								</Box>
								<Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
									{ticket.validatingCarrierName} · {getOperatorName(ticket.operator)} ·{' '}
									{formatDateTimeToShowUser(ticket.lastActivityAt)}
								</Typography>
							</Box>

							<Typography
								variant="body2"
								fontWeight={600}
								color={isCrossed ? 'success.main' : 'text.disabled'}
								sx={{ textAlign: 'right', whiteSpace: 'nowrap', px: 0.5 }}
							>
								{isCrossed ? fmt(ticket.netAmount) : '—'}
							</Typography>

							{isCrossed && activeCross && onUnbindCross ? (
								<Tooltip title="Desvincular">
									<IconButton
										size="small"
										color="warning"
										onClick={() => onUnbindCross(activeCross)}
										aria-label="Desvincular boleto"
									>
										<LinkOffIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							) : (
								<Box sx={{ width: 34 }} />
							)}

							{ticket.history.length > 1 ? (
								<Tooltip title={historyOpen ? 'Ocultar historial' : 'Ver historial'}>
									<IconButton
										size="small"
										onClick={() => setExpandedEticket(historyOpen ? false : ticket.eticket)}
										aria-expanded={historyOpen}
										sx={{
											transform: historyOpen ? 'rotate(180deg)' : 'none',
											transition: 'transform 0.2s',
										}}
									>
										<ExpandMoreIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							) : (
								<Box sx={{ width: 34 }} />
							)}
						</Box>

						{ticket.history.length > 1 && (
							<Collapse in={historyOpen}>
								<Box sx={{ px: 1.5, pb: 1, pt: 0 }}>
									<TicketHistoryTimeline ticket={ticket} getOperatorName={getOperatorName} />
								</Box>
							</Collapse>
						)}

						{ticket.history.length === 1 && (
							<Box sx={{ px: 1.5, pb: 0.75 }}>
								<Typography variant="caption" color="text.secondary">
									Cruzado · {formatDateTimeToShowUser(ticket.history[0].createdAt)} · {ticket.history[0].createdBy}
								</Typography>
							</Box>
						)}
					</Box>
				);
			})}
		</Box>
	);
};
